"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AnchorInputState = void 0;
const tslib_1 = require("tslib");
const core_shared_1 = require("@ikegps/core-shared");
const lodash_1 = require("lodash");
const mobx_1 = require("mobx");
const i18n_1 = require("../../i18n");
const bearing_input_state_1 = require("./bearing.input.state");
const distance_input_state_1 = require("./distance.input.state");
const input_select_1 = require("./input.select");
const input_state_1 = require("./input.state");
const PLACEMENT_TO_SPANS_COUNT_MAP = {
    Inline: 1,
    Bisect: 2,
    Other: 0
};
class AnchorInputState extends input_state_1.InputState {
    get value() {
        if (this.distance.value == null && this.bearing.value == null) {
            return null;
        }
        return {
            bearing: this.bearing.value,
            distance: this.distance.value,
            pitch: this.pitch,
            collectionTool: 'VECTOR',
            placement: this.placement.value,
            // we only want to return the correct number of spans based off the placement, ie. inline should only return 1 span
            relatedSpanIds: this.relatedSpans.map(x => x.value).slice(0, PLACEMENT_TO_SPANS_COUNT_MAP[this.placement.value])
        };
    }
    static equal(value1, value2) {
        if (value1 === value2) {
            return true;
        }
        if (value1 == null || value2 == null) {
            return false;
        }
        return distance_input_state_1.DistanceInputState.equal(value1.distance, value2.distance) &&
            bearing_input_state_1.BearingInputState.equal(value1.bearing, value2.bearing) &&
            value1.pitch === value2.pitch &&
            value1.placement === value2.placement;
    }
    constructor(field) {
        super(field.schema.displayName);
        this.field = field;
        this.cachedSpanFields = [];
        this._error = '';
        this.isLabelShown = false;
        this.distance = new distance_input_state_1.DistanceInputState('Distance');
        this.bearing = new bearing_input_state_1.BearingInputState('Bearing');
        this.placement = new input_select_1.SelectInputState('Placement');
        this.placement.setOptions([
            {
                key: (0, i18n_1.Translate)(i18n_1.T.ANCHOR_ALIGNMENT_INLINE),
                value: 'Inline'
            },
            {
                key: (0, i18n_1.Translate)(i18n_1.T.ANCHOR_ALIGNMENT_BISECT),
                value: 'Bisect'
            },
            {
                key: (0, i18n_1.Translate)(i18n_1.T.ANCHOR_ALIGNMENT_OTHER),
                value: 'Other'
            }
        ]);
        this.relatedSpans = [
            new input_select_1.SelectInputState((0, i18n_1.Translate)(i18n_1.T.ACCURATE_ANCHOR_RELATED_SPAN_TITLE, 'A')),
            new input_select_1.SelectInputState((0, i18n_1.Translate)(i18n_1.T.ACCURATE_ANCHOR_RELATED_SPAN_TITLE, 'B'))
        ];
        this.reaction(() => this.bearing.value, () => {
            if (this.bearing.focused) {
                this.placement.setValue('Other');
                this.relatedSpans.forEach(x => x.setValue(undefined));
            }
        });
    }
    populateRelatedSpansOptions(fields) {
        let options = [];
        this.cachedSpanFields = fields;
        fields.forEach((x) => {
            const spanLabel = x.parent && x.parent instanceof core_shared_1.CollectionModelFieldSubFormInstance
                ? x.parent.title
                : x.schema.displayName;
            options.push({
                key: spanLabel,
                value: x.id
            });
        });
        this.relatedSpans.forEach(x => x.setOptions(options));
    }
    setValue(val) {
        if (val == null) {
            this.distance.setValue(null);
            this.bearing.setValue(null);
            this.pitch = null;
            this.placement.setValue(null);
            this.setError(null);
            return;
        }
        this.distance.setValue(val.distance);
        this.pitch = val.pitch;
        this.placement.setValue(val.placement);
        const bearing = this.bearing.focused
            ? val.bearing
            : this.calculateBearingFromPlacement() || val.bearing;
        if (bearing !== this.cachedBearing) {
            this.bearing.setValue(bearing);
            this.cachedBearing = bearing;
        }
        this.relatedSpans[0].setValue((0, lodash_1.get)(val, ['relatedSpanIds', '0']));
        this.relatedSpans[1].setValue((0, lodash_1.get)(val, ['relatedSpanIds', '1']));
        switch (val.placement) {
            case 'Bisect':
                this.relatedSpans.forEach(x => x.setDisabled(false));
                break;
            case 'Inline':
                this.relatedSpans[0].setDisabled(false);
                this.relatedSpans[1].setDisabled(true);
                break;
            default:
                this.relatedSpans.forEach(x => x.setDisabled(true));
        }
        if (val.distance == null && val.bearing == null) {
            this.setError(null);
        }
        else if (val.distance == null) {
            this.setError(i18n_1.T.DISTANCE_MISSING);
        }
        else if (val.bearing == null) {
            this.setError(i18n_1.T.BEARING_MISSING);
        }
        else {
            this.setError(null);
        }
    }
    calculateBearingFromPlacement() {
        const placement = this.placement.value;
        let nextBearing;
        const values = this.relatedSpans.map(x => x.value);
        const temp = [];
        this.field.collection.forEachField(x => temp.push(x.id));
        if (placement === 'Bisect') {
            if (!values.filter(Boolean).length) {
                this.setError(i18n_1.T.ACCURATE_ANCHOR_RELATED_SPAN_NO_SELECTION_BISECT);
            }
            const spanRadians = values
                .map(fieldId => this.field.collection.getFieldDeep(fieldId))
                .map((x, index) => {
                const value = (0, lodash_1.get)(x, ['value', 'bearing']);
                if (!value) {
                    this.setError(i18n_1.T.ACCURATE_ANCHOR_RELATED_SPAN_NO_VALUE);
                }
                return value;
            })
                .sort();
            // ensure all values are defined and are numbers
            if (spanRadians.some(x => typeof x !== 'number')) {
                return undefined;
            }
            const [a, b] = spanRadians.map(x => core_shared_1.IkeAngle.toDegrees(x));
            // find the angle between the two bearings
            const theta = a - b;
            // find the bisect angle of theta relative to a.
            // basically we derive what half theta is (θ/2) and minus that value
            // from a to derive what the bisector is
            const bisect = a - (theta / 2);
            // We then want to ensure the anchor is placed on convex side of the angle
            // so we add 180 deg if θ < 180
            const result = Math.abs(theta) < 180 ? bisect + 180 : bisect;
            nextBearing = core_shared_1.IkeAngle.toRadians(result);
        }
        if (placement === 'Inline') {
            if (this.relatedSpans[0].value === undefined) {
                this.setError(i18n_1.T.ACCURATE_ANCHOR_RELATED_SPAN_NO_SELECTION_INLINE);
            }
            const relatedSpan = this.field.collection.getFieldDeep(this.relatedSpans[0].value);
            const bearing = (0, lodash_1.get)(relatedSpan, ['value', 'bearing']);
            if (!bearing) {
                this.setError(i18n_1.T.ACCURATE_ANCHOR_RELATED_SPAN_NO_VALUE);
            }
            nextBearing = core_shared_1.IkeAngle.toRadians(180) + bearing;
        }
        return nextBearing;
    }
    hasError() {
        return super.hasError() || this.distance.hasError() || this.bearing.hasError() || this.placement.hasError() || this.relatedSpans.some(x => x.hasError());
    }
    get error() {
        // Don't show an error message if one is already being shown
        if (this.distance.hasError() || this.bearing.hasError() || this.placement.hasError()) {
            return null;
        }
        if (!this.distance.value) { // distance is empty
            if (this.placement.value || this.bearing.value > 0) { // placement has a value or the bearing has an actual value
                return i18n_1.T.DISTANCE_MISSING;
            }
            return this._error; // everything is empty
        }
        else if (this.distance.value && this.bearing.value === null) { // does bearing have an actual value
            return i18n_1.T.BEARING_MISSING;
        }
        else if (this.placement.value === null && this.distance.value) { // does placement have a valid value
            return i18n_1.T.PLACEMENT_MISSING;
        }
        // everything has a valid value
        return this._error;
    }
    set error(msg) {
        this._error = msg || '';
    }
    destroy() {
        super.destroy();
        this.distance.destroy();
    }
}
exports.AnchorInputState = AnchorInputState;
tslib_1.__decorate([
    mobx_1.computed
], AnchorInputState.prototype, "value", null);
tslib_1.__decorate([
    mobx_1.action
], AnchorInputState.prototype, "setValue", null);
tslib_1.__decorate([
    mobx_1.computed
], AnchorInputState.prototype, "error", null);
//# sourceMappingURL=anchor.input.state.js.map